/**
 * BioTime Live Timelogs — the wire types and the PURE mappers behind the
 * Reports page's "Live Timelogs" report.
 *
 * The contract is ERPat's `modules/Biotime/specs/biotime-realtime-logs-contract.md`:
 * §8.1 is one row, §8.2 the envelope around a page of them, §8.5 the filter
 * options. This file only reads those shapes; it never measures, pairs or
 * re-orders punches (§9: "a pure consumer of §8"). The server already decided
 * which punch sits in which of the ten columns and whether the day is open.
 *
 * DEFENSIVE, like every mapper in `models.ts`: a null, a missing key, a wrong
 * type or an unexpected extra key never throws — it degrades to an empty value.
 * The endpoints are new and a stale server (or a cached HTML error page) must
 * not take the report down with it.
 *
 * TIMES ARE WALL CLOCKS. Every `*_local` value is a zone-less
 * `"2026-09-27 08:01:12"` in the BioTime connection's timezone (§4.1). It is
 * formatted by reading its DIGITS ({@link formatWallClockTime}) and is never run
 * through `new Date()`: that would re-read it in the DEVICE's zone, and a phone
 * set to another timezone would show the wrong hour. `*_utc` values are UTC and
 * are carried through untouched for anything that needs an instant.
 *
 * PERSONAL DATA. An employee code, a name and a punch time identify a person.
 * Nothing here logs, stores or reports them; neither may a caller.
 */
import { asRecord, toList, toPagination, type Json, type Pagination } from '@/services/api/http';

// ── Constants ────────────────────────────────────────────────────────────────

/**
 * The ten HR Detailed columns, in order (§1.3 `biotime_realtime_slots()`).
 * `in` and `out` travel as their own fields; the eight between them travel as
 * the `breaks` array.
 */
export const REALTIME_SLOT_NAMES = [
  'in',
  'am_out',
  'am_in',
  'lunch_out',
  'lunch_in',
  'pm_out',
  'pm_in',
  'extra_out',
  'extra_in',
  'out',
] as const;

export type RealtimeSlotName = (typeof REALTIME_SLOT_NAMES)[number];

/** The eight slots between `in` and `out` — the `breaks` array, in its fixed order. */
export const REALTIME_BREAK_SLOT_NAMES: readonly RealtimeSlotName[] = REALTIME_SLOT_NAMES.slice(1, 9);

/** What each column is called on screen. */
export const REALTIME_SLOT_LABELS: Readonly<Record<RealtimeSlotName, string>> = {
  in: 'Time in',
  am_out: 'AM out',
  am_in: 'AM in',
  lunch_out: 'Lunch out',
  lunch_in: 'Lunch in',
  pm_out: 'PM out',
  pm_in: 'PM in',
  extra_out: 'Extra out',
  extra_in: 'Extra in',
  out: 'Time out',
};

/**
 * A punch the server could not fit into the ten columns (§3.3 `slot_of` →
 * `'overflow'`). It is still listed — punches beyond capacity are counted,
 * never dropped — and the detail sheet calls it an "Extra punch": the wire word
 * `overflow` is an implementation term, not something to show an employee.
 */
export const REALTIME_OVERFLOW_SLOT = 'overflow';
export const REALTIME_OVERFLOW_LABEL = 'Extra punch';

/**
 * `punch_state` is a STRING enum (§4.1: "never cast"). BioTime's own meanings;
 * any other value is shown as sent.
 */
export const REALTIME_PUNCH_STATE_LABELS: Readonly<Record<string, string>> = {
  '0': 'Check in',
  '1': 'Check out',
  '2': 'Break out',
  '3': 'Break in',
  '4': 'Overtime in',
  '5': 'Overtime out',
};

/** The closed flag vocabulary (§1.4). Anything else maps to `null`. */
export type RealtimeRowFlag = 'odd_punch' | 'open_shift';

/** §5.2 / §8.2 — where a day's rows came from. */
export type RealtimeDaySource = 'cache' | 'stale' | 'none';

/** Seconds a 202 tells the client to wait when it says nothing itself (§1.1 `API_RETRY_AFTER`). */
export const REALTIME_DEFAULT_RETRY_AFTER_SECONDS = 3;

/**
 * §9 "Live refresh": page 1 is re-read every `filters.refresh_seconds`, never
 * faster than this.
 */
export const REALTIME_REFRESH_FLOOR_SECONDS = 30;

/** What `refresh_seconds` means when the server does not say (§8.5 example). */
export const REALTIME_DEFAULT_REFRESH_SECONDS = 60;

/** §1.2 `max_days` bounds. */
export const REALTIME_MAX_DAYS_MIN = 1;
export const REALTIME_MAX_DAYS_MAX = 31;

// ── View models ──────────────────────────────────────────────────────────────

/** One of the ten columns of a row. */
export interface RealtimeSlotVM {
  name: RealtimeSlotName;
  /** "Time in", "AM out", … */
  label: string;
  /** Wall clock in the BioTime timezone, `YYYY-MM-DD HH:mm:ss`; null when empty. */
  local: string | null;
  /** The same instant in UTC; null when empty. */
  utc: string | null;
  /** "8:01 AM", or '' when the column is empty. */
  timeLabel: string;
  filled: boolean;
}

/** Where a punch landed: one of the ten columns, or beyond them. */
export type RealtimePunchSlot = RealtimeSlotName | typeof REALTIME_OVERFLOW_SLOT;

/** One raw punch (§8.1 `punches[]`, present with `include=punches`). */
export interface RealtimePunchVM {
  /** null when the server named no slot this app knows. */
  slot: RealtimePunchSlot | null;
  /** "Lunch out", "Extra punch" (beyond the ten columns), or '' for an unknown slot. */
  slotLabel: string;
  /** "8:01 AM" — read from the wall-clock digits. */
  timeLabel: string;
  local: string | null;
  utc: string | null;
  /** BioTime's `punch_state`, a STRING — "0".."5" or whatever was sent. Never a number. */
  state: string;
  /** "Check in" … "Overtime out", or the raw state when unknown. */
  stateLabel: string;
  /** BioTime's verification method code (fingerprint, face, card …); null when absent. */
  verifyType: number | null;
  terminalSn: string;
  terminalAlias: string;
  area: string;
}

/** One employee-day (§8.1). */
export interface RealtimeLogRowVM {
  /** The wire `key` (`"2026-09-27_1042"`) — the list identity. */
  id: string;
  /** `YYYY-MM-DD`, the BioTime-local date; '' when the server sent none. */
  date: string;
  /** "Sun, Sep 27, 2026" — built from the date's digits. */
  dateLabel: string;
  empCode: string;
  biotimeName: string;
  /** The linked ERPat full name, else the BioTime name, else the employee code. */
  displayName: string;
  /** True when the employee code is bound to an ERPat user. */
  linked: boolean;
  /** ERPat user id as a string; '' when not linked (or not exposed). */
  userId: string;
  /** ERPat ID number; '' when not linked. */
  idNumber: string;
  /** ERPat job title; '' when not linked or not exposed. */
  jobTitle: string;
  /** ERPat department(s) when linked and known, else the BioTime department name. */
  department: string;
  biotimeDepartment: string;
  biotimeDepartmentCode: string;
  biotimePosition: string;
  biotimePositionCode: string;
  /** "8:01 AM" / '' — the `in` column. */
  inLabel: string;
  /** "5:02 PM" / '' — the `out` column (empty on an open shift). */
  outLabel: string;
  /** Always ten entries, in {@link REALTIME_SLOT_NAMES} order. */
  slots: RealtimeSlotVM[];
  punchCount: number;
  /** Punches beyond the ten columns. */
  overflowCount: number;
  /** The shift is still open (today, no closing punch yet). */
  isOpen: boolean;
  flag: RealtimeRowFlag | null;
  punches: RealtimePunchVM[];
}

/** One day of the window, as the cache served it (§8.2 `meta.days[]`). */
export interface RealtimeDayMetaVM {
  date: string;
  /** UTC `YYYY-MM-DD HH:mm:ss` of the last write; '' when never fetched. */
  fetchedAtUtc: string;
  source: RealtimeDaySource;
  /** A past day whose slice is final. */
  sealed: boolean;
  /** The day hit the punch cap (§1.1 `DAY_PUNCH_CAP`). */
  truncated: boolean;
}

/**
 * §8.4 `meta.access` / §8.5 `filters.access` — WHO this answer was scoped to
 * (Biotime 1.11.0). Both End-User routes read with the caller's ATTENDANCE
 * authority: the rows, the punches, the pickers and the total never exceed the
 * employees that authority lets the caller see.
 *
 *  - `scope`    — `all` (an administrator, or `attendance = all`) or `specific`
 *                 (selected members / teams, or the caller alone);
 *  - `selfOnly` — the authority is the caller alone (an empty selection or a
 *                 bare grant): the copy says "your punches", not "nobody";
 *  - `scopeKey` — an opaque digest of who-may-see-what. It changes whenever the
 *                 members, teams, mode or eligible bindings change. A CONSISTENCY
 *                 token only — never a credential, never stored: the report
 *                 compares it between answers and starts over when it moves.
 */
export interface RealtimeAccessVM {
  scope: 'all' | 'specific';
  selfOnly: boolean;
  scopeKey: string;
}

/** §8.2 `meta`. */
export interface RealtimeLogsMetaVM {
  page: number;
  perPage: number;
  /** null while the answer is incomplete (a 202). */
  total: number | null;
  /** False on a 202: some day in the window is still being fetched. */
  complete: boolean;
  /** Dates (`YYYY-MM-DD`) not yet servable. */
  pendingDays: string[];
  /** Seconds to wait before asking again; null when the server said nothing. */
  retryAfter: number | null;
  startDate: string;
  endDate: string;
  /** IANA zone of the BioTime connection — the zone every `*_local` is in. */
  timezone: string;
  /** Widest window the server accepts; null when not sent. */
  maxDays: number | null;
  days: RealtimeDayMetaVM[];
  /** Who the answer is scoped to; null when the server sent no (or a malformed) block. */
  access: RealtimeAccessVM | null;
}

/** One page of the report — what `biotimeRealtimeService.list()` returns. */
export interface RealtimeLogsPage {
  items: RealtimeLogRowVM[];
  pagination: Pagination;
  meta: RealtimeLogsMetaVM;
}

export interface RealtimeDepartmentOption {
  code: string;
  name: string;
}

export interface RealtimeEmployeeOption {
  empCode: string;
  name: string;
}

/** §8.5 — the pickers, plus the availability probe's answer. */
export interface RealtimeLogFiltersVM {
  /** `YYYY-MM-DD` in the BioTime timezone; '' when not sent. */
  today: string;
  timezone: string;
  /** Clamped to 1..31. */
  maxDays: number;
  /** How often re-reading can show anything new; never below 30. */
  refreshSeconds: number;
  departments: RealtimeDepartmentOption[];
  employees: RealtimeEmployeeOption[];
  /** True when the server capped the employee list — the CALLER's list, scoped before the cap. */
  employeesTruncated: boolean;
  /** Who the pickers are scoped to; null when the server sent no (or a malformed) block. */
  access: RealtimeAccessVM | null;
}

/** The query `GET /v1/api/biotime/live-timelogs` accepts, in the server's names. */
export interface RealtimeLogsQuery {
  start_date?: string;
  end_date?: string;
  emp_code?: string;
  dept_code?: string;
  page?: number;
  per_page?: number;
}

/** Transport facts the page mapper may use when `meta` is silent. */
export interface RealtimeLogsResponseHints {
  /** HTTP status of the answer — a 202 means incomplete. */
  status?: number;
  /** The envelope-level `meta` (the contract nests it in `data`; tolerated either way). */
  envelopeMeta?: Record<string, unknown>;
  /** The `Retry-After` header, as the SDK parsed it. */
  retryAfterHeader?: number | null;
}

// ── Primitive readers ────────────────────────────────────────────────────────

/** A trimmed string, or '' — numbers are accepted and stringified, nothing else. */
function text(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/** The first key of `rec` holding a usable {@link text}. */
function textOf(rec: Json, keys: string[]): string {
  for (const key of keys) {
    const value = text(rec[key]);
    if (value !== '') return value;
  }
  return '';
}

/** A finite number from a number or a numeric string; null otherwise. */
function numberOf(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** A non-negative whole number, or `fallback`. */
function countOf(value: unknown, fallback: number): number {
  const n = numberOf(value);
  return n !== null && n >= 0 ? Math.floor(n) : fallback;
}

/** true / 1 / "1" / "true" / "yes" → true; false-ish → false; anything else → null. */
function boolOf(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === '1' || s === 'true' || s === 'yes') return true;
    if (s === '0' || s === 'false' || s === 'no' || s === '') return false;
  }
  return null;
}

/** A wall-clock / UTC stamp string, or null for empty and non-strings. */
function stampOf(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  return s === '' ? null : s;
}

function isSlotName(value: unknown): value is RealtimeSlotName {
  return typeof value === 'string' && (REALTIME_SLOT_NAMES as readonly string[]).includes(value);
}

// ── Civil dates, from digits only ────────────────────────────────────────────

const CIVIL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function civilParts(value: unknown): { year: number; month: number; day: number } | null {
  if (typeof value !== 'string') return null;
  const match = CIVIL_DATE_RE.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

/** True for a real `YYYY-MM-DD` calendar date ('2026-02-30' is not one). */
export function isCivilDate(value: unknown): value is string {
  return civilParts(value) !== null;
}

/**
 * Days since 1970-01-01 for a civil date (Hinnant's `days_from_civil`), or null.
 * Pure arithmetic — no `Date`, so no timezone and no DST can move it.
 */
export function civilDayNumber(value: unknown): number | null {
  const parts = civilParts(value);
  if (!parts) return null;
  const { month, day } = parts;
  const year = month <= 2 ? parts.year - 1 : parts.year;
  const era = Math.floor(year / 400);
  const yearOfEra = year - era * 400;
  const shiftedMonth = (month + 9) % 12; // March = 0
  const dayOfYear = Math.floor((153 * shiftedMonth + 2) / 5) + day - 1;
  const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
  return era * 146097 + dayOfEra - 719468;
}

/** "Sun, Sep 27, 2026" — from the digits; '' for anything that is not a real date. */
export function formatCivilDateLabel(value: unknown): string {
  const parts = civilParts(value);
  const dayNumber = civilDayNumber(value);
  if (!parts || dayNumber === null) return '';
  // 1970-01-01 was a Thursday (index 4).
  const weekday = WEEKDAY_SHORT[(((dayNumber + 4) % 7) + 7) % 7] ?? '';
  const month = MONTH_SHORT[parts.month - 1] ?? '';
  return `${weekday}, ${month} ${parts.day}, ${parts.year}`;
}

// ── Wall clock ───────────────────────────────────────────────────────────────

/**
 * `"YYYY-MM-DD HH:mm[:ss]"` (or a bare `"HH:mm[:ss]"`) → `"8:01 AM"`.
 *
 * Reads the DIGITS. It never builds a `Date`: the value is a wall clock in the
 * BioTime connection's zone, and a `Date` would re-read it in the device's.
 * A value carrying a zone (`Z`, `+08:00`) is an instant, not a wall clock, and
 * is refused ('') rather than shown at the wrong hour.
 */
export function formatWallClockTime(value: unknown): string {
  if (typeof value !== 'string') return '';
  const match = /^(?:\d{4}-\d{2}-\d{2}[ T])?(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(value.trim());
  if (!match) return '';
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return '';
  const meridian = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${match[2]} ${meridian}`;
}

// ── Row mapping ──────────────────────────────────────────────────────────────

function slotVM(name: RealtimeSlotName, local: string | null, utc: string | null): RealtimeSlotVM {
  const timeLabel = formatWallClockTime(local);
  return {
    name,
    label: REALTIME_SLOT_LABELS[name],
    local,
    utc,
    timeLabel,
    filled: local !== null || utc !== null,
  };
}

/**
 * The ten columns of a row, always ten, in order.
 *
 * The eight middle ones come from `breaks` — matched by each entry's own
 * `slot` name, falling back to its position when the name is missing or
 * unknown (the array's order is fixed by contract). A missing or short array
 * leaves the rest empty. A server that sent the §4.4 `slots` map instead is
 * read too.
 */
function mapSlots(rec: Json): RealtimeSlotVM[] {
  const breaks = new Map<RealtimeSlotName, Json>();
  const rawBreaks = rec.breaks;
  if (Array.isArray(rawBreaks)) {
    rawBreaks.forEach((entry, index) => {
      const item = asRecord(entry);
      const named = isSlotName(item.slot) && REALTIME_BREAK_SLOT_NAMES.includes(item.slot) ? item.slot : null;
      const name = named ?? REALTIME_BREAK_SLOT_NAMES[index];
      if (name && !breaks.has(name)) breaks.set(name, item);
    });
  }
  const slotsMap = asRecord(rec.slots);

  return REALTIME_SLOT_NAMES.map((name) => {
    if (name === 'in' || name === 'out') {
      const fromMap = asRecord(slotsMap[name]);
      const local = stampOf(rec[`${name}_local`]) ?? stampOf(fromMap.local);
      const utc = stampOf(rec[`${name}_utc`]) ?? stampOf(fromMap.utc);
      return slotVM(name, local, utc);
    }
    const entry = breaks.get(name) ?? asRecord(slotsMap[name]);
    return slotVM(name, stampOf(entry.local), stampOf(entry.utc));
  });
}

/** §8.1 `punches[]` entry → view model. */
export function mapRealtimePunch(raw: unknown): RealtimePunchVM {
  const rec = asRecord(raw);
  const rawSlot = text(rec.slot);
  const slot: RealtimePunchSlot | null = isSlotName(rawSlot)
    ? rawSlot
    : rawSlot === REALTIME_OVERFLOW_SLOT
      ? REALTIME_OVERFLOW_SLOT
      : null;
  const slotLabel = slot === null ? '' : slot === REALTIME_OVERFLOW_SLOT ? REALTIME_OVERFLOW_LABEL : REALTIME_SLOT_LABELS[slot];

  const local = stampOf(rec.punch_time_local) ?? stampOf(rec.local);
  const utc = stampOf(rec.punch_time_utc) ?? stampOf(rec.utc);

  // A STRING enum. A number that slipped through is stringified (never the
  // other way round — "0" stays "0", it is never read as a count).
  const rawState = rec.punch_state ?? rec.state;
  const state = typeof rawState === 'string' ? rawState.trim() : text(rawState);
  const stateLabel = REALTIME_PUNCH_STATE_LABELS[state] ?? state;

  const verify = numberOf(rec.verify_type ?? rec.verify);

  return {
    slot,
    slotLabel,
    timeLabel: formatWallClockTime(local),
    local,
    utc,
    state,
    stateLabel,
    verifyType: verify !== null && Number.isInteger(verify) ? verify : null,
    terminalSn: textOf(rec, ['terminal_sn', 'sn']),
    terminalAlias: textOf(rec, ['terminal_alias', 'alias']),
    area: textOf(rec, ['area', 'area_alias']),
  };
}

function flagOf(value: unknown): RealtimeRowFlag | null {
  return value === 'odd_punch' || value === 'open_shift' ? value : null;
}

/** A `{ code, name }` pair that may also arrive flat or as a bare string. */
function codeName(value: unknown): { code: string; name: string } {
  if (typeof value === 'string') return { code: '', name: value.trim() };
  const rec = asRecord(value);
  return { code: textOf(rec, ['code']), name: textOf(rec, ['name']) };
}

/**
 * §8.1 row → view model. Never throws; `index` only keys a row the server sent
 * with no key and no date/code to build one from.
 */
export function mapRealtimeLogRow(raw: unknown, index = 0): RealtimeLogRowVM {
  const rec = asRecord(raw);

  const dateRaw = textOf(rec, ['date_local', 'date']);
  const date = isCivilDate(dateRaw) ? dateRaw : '';
  const empCode = textOf(rec, ['emp_code', 'employee_code']);
  // §8.1 sends `biotime_name`; the §4.4 row it is shaped from also carries the
  // two halves, so a server that forwarded those instead still names the person.
  const biotimeName =
    textOf(rec, ['biotime_name', 'name']) ||
    [textOf(rec, ['first_name']), textOf(rec, ['last_name'])].filter((part) => part !== '').join(' ');

  const dept = codeName(rec.biotime_department);
  const position = codeName(rec.biotime_position);
  const biotimeDepartment = dept.name || textOf(rec, ['dept_name']);
  const biotimeDepartmentCode = dept.code || textOf(rec, ['dept_code']);
  const biotimePosition = position.name || textOf(rec, ['position_name']);
  const biotimePositionCode = position.code || textOf(rec, ['position_code']);

  // `erpat` is null when the code is not bound; `minimal` carries only the id
  // pair, `full` adds name / title / department (§8.1).
  const erpat = asRecord(rec.erpat);
  const userIdNum = numberOf(erpat.user_id);
  const userId = userIdNum !== null && userIdNum > 0 ? String(Math.trunc(userIdNum)) : '';
  const idNumber = textOf(erpat, ['id_number']);
  const fullName = textOf(erpat, ['full_name']);
  const jobTitle = textOf(erpat, ['job_title']);
  const erpatDepartment = textOf(erpat, ['department']);
  const linked = userId !== '' || idNumber !== '' || fullName !== '';

  const slots = mapSlots(rec);
  const punches = Array.isArray(rec.punches) ? rec.punches.map(mapRealtimePunch) : [];
  const overflowCount = countOf(rec.overflow_count, 0);
  const filledSlots = slots.filter((s) => s.filled).length;
  const punchCount = countOf(
    rec.punch_count,
    punches.length > 0 ? punches.length : filledSlots + overflowCount,
  );

  const key = textOf(rec, ['key', 'id']);
  const id = key || (date && empCode ? `${date}_${empCode}` : `row-${index}`);

  return {
    id,
    date,
    dateLabel: formatCivilDateLabel(date),
    empCode,
    biotimeName,
    displayName: fullName || biotimeName || empCode,
    linked,
    userId,
    idNumber,
    jobTitle,
    // An unlinked row (or a linked one ERPat files under no department) falls
    // back to where BioTime puts the person.
    department: (linked && erpatDepartment) || biotimeDepartment,
    biotimeDepartment,
    biotimeDepartmentCode,
    biotimePosition,
    biotimePositionCode,
    inLabel: slots[0]?.timeLabel ?? '',
    outLabel: slots[slots.length - 1]?.timeLabel ?? '',
    slots,
    punchCount,
    overflowCount,
    isOpen: boolOf(rec.is_open) === true,
    flag: flagOf(rec.flag),
    punches,
  };
}

// ── Meta / page ──────────────────────────────────────────────────────────────

function daySourceOf(value: unknown): RealtimeDaySource {
  return value === 'cache' || value === 'stale' || value === 'none' ? value : 'none';
}

/**
 * One `meta.days[]` entry. §8.2 sends a LIST of `{ date, … }`; the §4.5
 * provenance it is shaped from is a MAP keyed by date, so a server that
 * forwarded that map is read too — the key stands in for a missing `date`.
 */
function mapDayMeta(raw: unknown, keyDate = ''): RealtimeDayMetaVM | null {
  const rec = asRecord(raw);
  const date = textOf(rec, ['date']) || keyDate;
  if (!isCivilDate(date)) return null;
  return {
    date,
    fetchedAtUtc: textOf(rec, ['fetched_at_utc']),
    source: daySourceOf(rec.source),
    sealed: boolOf(rec.sealed) === true,
    truncated: boolOf(rec.truncated) === true,
  };
}

/**
 * §8.4 / §8.5 `access` → view model, validated as a WHOLE: an unknown `scope`, a
 * missing or empty `scope_key`, or an unreadable `self_only` makes the block
 * null — never a partial block, and never a manufactured `all`. A null block is
 * the report's cue to treat the answer conservatively (re-read rather than
 * merge), not a licence to assume the widest scope.
 */
export function mapRealtimeAccess(raw: unknown): RealtimeAccessVM | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const rec = asRecord(raw);
  const scope = text(rec.scope);
  if (scope !== 'all' && scope !== 'specific') return null;
  const selfOnly = boolOf(rec.self_only);
  if (selfOnly === null) return null;
  const scopeKey = typeof rec.scope_key === 'string' ? rec.scope_key.trim() : '';
  if (scopeKey === '') return null;
  return { scope, selfOnly, scopeKey };
}

/**
 * §8.2 `meta` → view model.
 *
 * `complete` is read from the meta; when a server omits it, a 202 status says
 * incomplete and otherwise an empty `pending_days` says complete.
 */
export function mapRealtimeLogsMeta(raw: unknown, hints: RealtimeLogsResponseHints = {}): RealtimeLogsMetaVM {
  const rec = asRecord(raw);

  const pendingDays = Array.isArray(rec.pending_days)
    ? rec.pending_days.map((d) => text(d)).filter((d) => isCivilDate(d))
    : [];

  const completeFlag = boolOf(rec.complete);
  const complete =
    completeFlag !== null
      ? completeFlag
      : hints.status === 202
        ? false
        : pendingDays.length === 0;

  const totalRaw = numberOf(rec.total);
  const retryRaw = numberOf(rec.retry_after) ?? numberOf(hints.retryAfterHeader);
  const maxDaysRaw = numberOf(rec.max_days);
  const page = numberOf(rec.page);
  const perPage = numberOf(rec.per_page);

  return {
    page: page !== null && page >= 1 ? Math.floor(page) : 1,
    perPage: perPage !== null && perPage >= 1 ? Math.floor(perPage) : 25,
    total: totalRaw !== null && totalRaw >= 0 ? Math.floor(totalRaw) : null,
    complete,
    pendingDays,
    retryAfter: retryRaw !== null && retryRaw >= 0 ? retryRaw : null,
    startDate: isCivilDate(text(rec.start_date)) ? text(rec.start_date) : '',
    endDate: isCivilDate(text(rec.end_date)) ? text(rec.end_date) : '',
    timezone: textOf(rec, ['timezone']),
    maxDays: maxDaysRaw !== null && maxDaysRaw >= 1 ? Math.floor(maxDaysRaw) : null,
    days: mapDays(rec.days),
    access: mapRealtimeAccess(rec.access),
  };
}

/** `meta.days` as a list (§8.2) or a date-keyed map (§4.5), oldest first, one entry per date. */
function mapDays(value: unknown): RealtimeDayMetaVM[] {
  const entries: Array<[string, unknown]> = Array.isArray(value)
    ? value.map((entry): [string, unknown] => ['', entry])
    : Object.entries(asRecord(value));
  const byDate = new Map<string, RealtimeDayMetaVM>();
  for (const [keyDate, entry] of entries) {
    const day = mapDayMeta(entry, keyDate);
    if (day && !byDate.has(day.date)) byDate.set(day.date, day);
  }
  // `YYYY-MM-DD` sorts lexically in calendar order.
  return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/**
 * A whole §8.2 answer → `{ items, pagination, meta }`.
 *
 * Pagination is built the way `teamAttendanceService.list()` builds it, with
 * two corrections for this endpoint: `total` is 0 while the server's is null
 * (an incomplete answer has no rows, so a paged list must not think there is a
 * page 2), and `totalPages` is derived from `total`, which the server sends
 * without a page count.
 */
export function mapRealtimeLogsPage(data: unknown, hints: RealtimeLogsResponseHints = {}): RealtimeLogsPage {
  const rec = asRecord(data);
  const metaRaw = rec.meta && typeof rec.meta === 'object' ? rec.meta : hints.envelopeMeta;
  const meta = mapRealtimeLogsMeta(metaRaw, hints);

  const listed = Array.isArray(rec.live_timelogs)
    ? (rec.live_timelogs as unknown[])
    : toList<unknown>(data);
  const items = listed.map((row, i) => mapRealtimeLogRow(row, i));

  const base = toPagination(asRecord(metaRaw), items.length);
  const total = meta.total ?? 0;
  const perPage = meta.perPage;
  const pagination: Pagination = {
    ...base,
    page: meta.page,
    perPage,
    total,
    totalPages: total > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1,
  };

  return { items, pagination, meta };
}

// ── Filters (§8.5) ───────────────────────────────────────────────────────────

/** §8.5 `filters` → view model. */
export function mapRealtimeLogFilters(data: unknown): RealtimeLogFiltersVM {
  const top = asRecord(data);
  const rec = top.filters && typeof top.filters === 'object' && !Array.isArray(top.filters) ? asRecord(top.filters) : top;

  const today = text(rec.today);
  const maxDaysRaw = numberOf(rec.max_days);
  const maxDays =
    maxDaysRaw === null
      ? REALTIME_MAX_DAYS_MIN
      : Math.min(REALTIME_MAX_DAYS_MAX, Math.max(REALTIME_MAX_DAYS_MIN, Math.floor(maxDaysRaw)));
  const refreshRaw = numberOf(rec.refresh_seconds);
  const refreshSeconds = Math.max(
    REALTIME_REFRESH_FLOOR_SECONDS,
    refreshRaw !== null && refreshRaw > 0 ? Math.floor(refreshRaw) : REALTIME_DEFAULT_REFRESH_SECONDS,
  );

  const departments: RealtimeDepartmentOption[] = [];
  const seenDepts = new Set<string>();
  for (const entry of Array.isArray(rec.departments) ? rec.departments : []) {
    const item = asRecord(entry);
    const code = textOf(item, ['code', 'dept_code']);
    if (code === '' || seenDepts.has(code)) continue;
    seenDepts.add(code);
    departments.push({ code, name: textOf(item, ['name', 'dept_name']) || code });
  }

  const employees: RealtimeEmployeeOption[] = [];
  const seenEmps = new Set<string>();
  for (const entry of Array.isArray(rec.employees) ? rec.employees : []) {
    const item = asRecord(entry);
    const empCode = textOf(item, ['emp_code', 'code']);
    if (empCode === '' || seenEmps.has(empCode)) continue;
    seenEmps.add(empCode);
    employees.push({ empCode, name: textOf(item, ['name', 'full_name']) || empCode });
  }

  return {
    today: isCivilDate(today) ? today : '',
    timezone: textOf(rec, ['timezone']),
    maxDays,
    refreshSeconds,
    departments,
    employees,
    employeesTruncated: boolOf(rec.employees_truncated) === true,
    access: mapRealtimeAccess(rec.access),
  };
}

// ── Query ────────────────────────────────────────────────────────────────────

/**
 * The query string for `GET /v1/api/biotime/live-timelogs`, in the server's
 * snake_case names. Empty filters are OMITTED (never sent as `""`), a page
 * number is sent only when it is a positive whole number, and `include=punches`
 * is always asked for — the detail sheet reads the raw punches off the row.
 */
export function realtimeLogsQueryParams(query: RealtimeLogsQuery = {}): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  const put = (key: keyof RealtimeLogsQuery, value: unknown): void => {
    const v = text(value);
    if (v !== '') out[key] = v;
  };
  put('start_date', query.start_date);
  put('end_date', query.end_date);
  put('emp_code', query.emp_code);
  put('dept_code', query.dept_code);
  const whole = (value: unknown): number | null =>
    typeof value === 'number' && Number.isInteger(value) && value >= 1 ? value : null;
  const page = whole(query.page);
  const perPage = whole(query.per_page);
  if (page !== null) out.page = page;
  if (perPage !== null) out.per_page = perPage;
  out.include = 'punches';
  return out;
}
